import { DOCUMENT } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    Inject,
    NgZone,
    OnDestroy,
    OnInit,
    Renderer2
} from '@angular/core';
import {
    ApplicationFacade,
    FavoritesFacade,
    JourneyDirection,
    JourneyPlannerItineraryTransportMode,
    MapCoordinates,
    MapIcons,
    OpenTripPlannerFacade,
    OpenTripPlannerService,
    BusLocation,
    RealtimeFacade,
    RealtimeObj,
    StopMarker
} from '@ridango/components';
import { LatLng } from 'leaflet';
import { NavigationStart, Router } from '@angular/router';
import { BaseComponent, LatLng as LatLngRidango } from '@ridango/core';
import { filter, take, takeUntil } from 'rxjs/operators';
import Env from '@ridango/core/env';
import { TranslateService } from '@ngx-translate/core';
import {
    IconSizeEnum,
    IconUtils,
    MapApiService,
    JourneyPlannerMapService,
    MarkerType,
    MapIconConfigs,
    IconSizeOrderEnum
} from '@ridango/widgets';
import { BehaviorSubject, combineLatest, timer } from 'rxjs';
import { StatusBarService } from '@ridango/core/lib/services/status-bar.service';

@Component({
    templateUrl: './journey-planner-page.component.html',
    styleUrls: ['./journey-planner-page.component.scss']
})
export class JourneyPlannerPageComponent extends BaseComponent implements OnInit, OnDestroy, AfterViewInit {
    private location: LatLng;

    readonly MarkerType = MarkerType;
    readonly IconSizeEnum = IconSizeEnum;
    readonly IconSizeOrderEnum = IconSizeOrderEnum;
    readonly stopsVisibleFromZoomSize: IconSizeEnum =
        this.env.MAP.STOPS_VISIBLE_FROM_ZOOM_SIZE ?? this.IconSizeEnum.small;

    search$ = this.openTripPlannerFacade.search$;
    search;
    mapJourneyPlan$ = this.openTripPlannerFacade.journeyMapPlan$;
    busLocations$ = this.realtime.vehicleLocations$;
    showFullMap$ = this.journeyPlannerMapService.isFullMapView$;
    stops$ = this.openTripPlannerFacade.stops$;

    mapCenter: MapCoordinates = this.env.MAP.COORDINATES;
    iconOrigin = MapIcons('journeyPlannerOrigin', this.env.THEME_NAME);
    iconDestination = MapIcons('journeyPlannerDestination', this.env.THEME_NAME);
    iconStop = MapIcons('stop', this.env.THEME_NAME);
    iconFavoriteStop = MapIcons('favorite-stop', this.env.THEME_NAME);

    journeyPlanClickedStop: StopMarker | null = null;
    clickedStop = null;

    allFormattedStops: StopMarker[] = [];
    allFavoriteStops: StopMarker[] = [];
    busLocation = [];
    private allBusLocations: BusLocation[] = [];
    selectedItineraryBusses = [];
    selectedItineraryBusses$ = new BehaviorSubject<RealtimeObj[]>([]);
    favoriteStops$ = this.favoritesFacade.favoriteStops$;
    assetsPath$ = this.applicationFacade.assetsPath$;

    formattedStops = [];
    favoriteStops: StopMarker[] = [];
    zoomIconSizeValue: IconSizeEnum;
    zoomValue: number;
    lastCheckedLocation: LatLngRidango = null;

    journeyStartTime: number;
    journeyEndTime: number;
    journeyStartStopName: string;
    journeyEndStopName: string;
    originMarkerCoordinate: LatLngRidango;
    destinationMarkerCoordinate: LatLngRidango;
    mapJourneyPlan = null;
    isContrast$ = this.applicationFacade.highContrast$;
    hasLocationError = false;
    mapIcons: MapIconConfigs;

    constructor(
        @Inject('ENV') public env: Env,
        @Inject(DOCUMENT) private document: Document,
        private renderer: Renderer2,
        private openTripPlannerFacade: OpenTripPlannerFacade,
        private realtime: RealtimeFacade,
        private statusBarColor: StatusBarService,
        private router: Router,
        private journeyPlannerMapService: JourneyPlannerMapService,
        private zone: NgZone,
        private translate: TranslateService,
        private mapApi: MapApiService,
        private favoritesFacade: FavoritesFacade,
        private openTripPlannerService: OpenTripPlannerService,
        private applicationFacade: ApplicationFacade,
        private cdRef: ChangeDetectorRef
    ) {
        super();
    }

    ngAfterViewInit(): void {
        this.renderer.removeClass(this.document.body, 'no-margin');
    }

    ngOnInit(): void {
        this.renderer.addClass(this.document.body, 'main-full-height');
        this.openTripPlannerFacade.getAllAlerts();
        this.openTripPlannerFacade.getAllStops();

        combineLatest([this.mapJourneyPlan$, this.search$])
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe(([plan, search]) => {
                this.mapJourneyPlan = plan;
                this.originMarkerCoordinate = plan?.journeyOriginLocation || search?.origin?.coordinates;
                this.destinationMarkerCoordinate = plan?.journeyDestinationLocation || search?.destination?.coordinates;
                this.cdRef.detectChanges();
            });

        this.router.events
            .pipe(
                takeUntil(this.componentDestroyed$),
                filter((event) => event instanceof NavigationStart)
            )
            .subscribe((event: NavigationStart) => {
                if (
                    event.url?.split('/')?.pop() === 'journey-planner' &&
                    this.env.JOURNEY_PLANNER.CLEAR_JP_AFTER_LEAVE
                ) {
                    this.openTripPlannerFacade.clearSearch();
                }
            });

        combineLatest([this.stops$, this.favoriteStops$])
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe(([stops, favoriteStops]) => {
                this.allFormattedStops = stops.map((s) => ({
                    ...s,
                    latlng: { lat: s.lat, lng: s.lon }
                }));
                this.allFavoriteStops = this.allFormattedStops.filter(
                    (s) => !!favoriteStops.find((fs) => fs.stopId === s.id)
                );
                this.setStopMarkers();
            });

        this.openTripPlannerFacade.location$.pipe(takeUntil(this.componentDestroyed$)).subscribe((location) => {
            if (!location?.coordinates || location.hasError) {
                return;
            }

            this.lastCheckedLocation = location.coordinates;
        });

        this.search$.pipe(takeUntil(this.componentDestroyed$)).subscribe((search) => {
            this.search = search;
        });

        this.busLocations$.pipe(takeUntil(this.componentDestroyed$)).subscribe((locations) => {
            this.allBusLocations = locations as BusLocation[];
            this.applySelectedItineraryBusFilter();
            this.cdRef.detectChanges();
        });

        this.selectedItineraryBusses$.pipe(takeUntil(this.componentDestroyed$)).subscribe(() => {
            this.applySelectedItineraryBusFilter();
            this.cdRef.detectChanges();
        });

        this.openTripPlannerFacade.selectedItineraryForDetails$
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((itinerary) => {
                if (itinerary) {
                    this.journeyStartTime = itinerary.startTime + (itinerary.journeyStartDepartureDelay || 0) * 1000;
                    this.journeyEndTime = itinerary.endTime + (itinerary.journeyEndArrivalDelay || 0) * 1000;
                    this.journeyStartStopName = undefined;
                    this.journeyEndStopName = undefined;

                    const legMode = JourneyPlannerItineraryTransportMode;
                    const legs = itinerary.legs;
                    const legsLength = legs.length;
                    this.selectedItineraryBusses = legs.filter((leg) => leg.mode === legMode.Bus);
                    this.selectedItineraryBusses$.next(this.selectedItineraryBusses);

                    const setJourneyStartStopName =
                        legs[0].mode !== legMode.Walk || legs[1].startTime === legs[0].startTime;
                    if (setJourneyStartStopName) {
                        this.journeyStartStopName =
                            legs[0].mode !== legMode.Walk ? legs[0].from.name : legs[1].from.name;
                    }

                    const setJourneyEndStopName =
                        legs[legsLength - 1].mode !== legMode.Walk ||
                        legs[legsLength - 2].endTime === legs[legsLength - 1].endTime;
                    if (setJourneyEndStopName) {
                        this.journeyEndStopName =
                            legs[legsLength - 1].mode !== legMode.Walk
                                ? legs[legsLength - 1].to.name
                                : legs[legsLength - 2].to.name;
                    }
                }
            });
    }

    ngOnDestroy(): void {
        this.renderer.removeClass(this.document.body, 'main-full-height');
        if (this.env.JOURNEY_PLANNER.CLEAR_JP_AFTER_LEAVE) {
            const currentUrl = this.router.url;
            const isStillInJourneyPlanner = currentUrl.includes('/journey-planner');
            const isInMobileMenu = currentUrl.includes('/mobile-menu');

            if (!isStillInJourneyPlanner && !isInMobileMenu) {
                this.openTripPlannerFacade.clearSearch();
            }
        }
        super.ngOnDestroy();
    }

    get isItineraryDetailPageActive() {
        return this.router.url.includes('journey-planner/itinerary/details');
    }

    onZoomChanged(zoom: number): void {
        this.zoomValue = zoom;
        this.zoomIconSizeValue = IconUtils.getIconSize(zoom);
        this.mapIcons = IconUtils.calculateMapIconsSize(zoom);
    }

    onPointClicked(coordinates): void {
        this.location = new LatLng(coordinates.lat, coordinates.lng);
    }

    public backToViewMap() {
        this.journeyPlannerMapService.setViewMap(false);
        this.statusBarColor.showStatusBar();
    }

    showFullMap() {
        if (!this.env.JOURNEY_PLANNER?.SHOW_FULL_MAP) return;

        this.journeyPlannerMapService.toggleViewMap();
        if (!this.journeyPlannerMapService.valueMapView()) {
            this.statusBarColor.showStatusBar();
            return;
        }
        timer(500)
            .pipe(take(1), takeUntil(this.componentDestroyed$))
            .subscribe(() => {
                this.statusBarColor.hideStatusBar();
            });
    }

    onStopClicked(stop): void {
        this.clickedStop = stop;
    }

    onDepartureClicked(name?: string, coordinates?: LatLng): void {
        if (name && coordinates) {
            this.openTripPlannerFacade.setSearchAddress(
                JourneyDirection.Origin,
                name,
                new LatLng(coordinates.lat, coordinates.lng),
                false
            );
        } else {
            this.openTripPlannerFacade.setSearchAddress(
                JourneyDirection.Origin,
                this.translate.instant('LOCATION_FROM_MAP'),
                this.location,
                false,
                true
            );
        }
    }

    onDestinationClicked(name?: string, coordinates?: LatLng): void {
        if (name && coordinates) {
            this.openTripPlannerFacade.setSearchAddress(
                JourneyDirection.Destination,
                name,
                new LatLng(coordinates.lat, coordinates.lng),
                false
            );
        } else {
            this.openTripPlannerFacade.setSearchAddress(
                JourneyDirection.Destination,
                'LOCATION_FROM_MAP',
                this.location,
                false,
                true
            );
        }
    }

    onPlanStopClick(stop) {
        this.zone.run(() => (this.journeyPlanClickedStop = stop));
    }

    private normalizeTripId(tripId: string): string {
        if (this.env.OTP_INDEX) {
            const prefix = `${this.env.OTP_INDEX}:`;
            if (tripId.startsWith(prefix)) {
                return tripId.replace(prefix, '');
            }
        }
        return tripId;
    }

    private applySelectedItineraryBusFilter(): void {
        if (this.selectedItineraryBusses.length && this.env.JOURNEY_PLANNER?.SHOW_SELECTED_ITINERARY_BUSSES) {
            this.busLocation = this.allBusLocations.filter((bus) =>
                this.selectedItineraryBusses.some(
                    (selectedBusses) => this.normalizeTripId(selectedBusses.tripId) === bus.trip_id
                )
            );
        } else {
            this.busLocation = this.allBusLocations;
        }
    }

    setStopMarkers() {
        this.formattedStops = this.allFormattedStops.filter((s) => !this.allFavoriteStops.find((fs) => fs.id === s.id));
        this.favoriteStops = this.allFormattedStops.filter((s) => this.allFavoriteStops.find((fs) => fs.id === s.id));
        this.cdRef.detectChanges();
    }
}
